import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Tooltip from '../components/ui/Tooltip';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import {
  ChevronDown, ChevronLeft, ChevronRight, ArrowUpDown, Home, Search,
  RefreshCw, XCircle, CheckCircle, Ban, AlertTriangle, List, ShoppingBasket,
  ClipboardList, BookOpen, Info, ChevronsUpDown, ChevronsDownUp,
} from 'lucide-react';
import Dropdown from '../components/ui/Dropdown';
import Input from '../components/ui/Input';
import ScrollText from '../components/ui/ScrollText';
import { cn } from '../utils/cn';
import { getBrandClasses } from '../config/tenantConfig';
import ordersService from '../services/ordersService';
import { useActingAs } from '../context/ActingAsContext';
import type { OrdersApiRow, OrdersApiResponse } from '../types';

type OrdersViewMode = 'all' | 'byBasket';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// The orderbook `time` is a RAW broker string already in IST wall-clock, often
// naive (no offset) — e.g. Zebu/Finvasia emit "%Y-%m-%d %H:%M:%S". Parsing it and
// re-projecting into a timezone would MIS-SHIFT it for non-IST viewers, so we
// parse-local + render-local (a pure reformat) to preserve the literal IST digits.
// (Position fill/exit timestamps ARE offset-carrying and use istParts instead.)
function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch {
    return iso;
  }
}

const statusBadgeVariant: Record<string, 'success' | 'danger' | 'warning' | 'default' | 'info'> = {
  SUCCESS: 'success',
  OPEN: 'info',
  FAILED: 'danger',
  CANCELLED: 'danger',
  PARTIAL: 'warning',
};

const statusLabels: Record<string, string> = {
  SUCCESS: 'COMPLETE',
  OPEN: 'OPEN',
  FAILED: 'REJECTED',
  CANCELLED: 'CANCELLED',
  PARTIAL: 'TRIGGER_PENDING',
};

const TH = 'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider';

type TimeSortDir = 'desc' | 'asc';

// --- Mobile Order Card ---

function MobileOrderCard({ order: r }: { order: OrdersApiRow }) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
      {/* Row 1: Symbol + Status + Price */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{r.symbol}</span>
          {r.exchange && <span className="text-[10px] text-gray-400">{r.exchange}</span>}
          <Badge variant={statusBadgeVariant[r.status] || 'default'} size="sm">
            {statusLabels[r.status] || r.status}
          </Badge>
        </div>
        <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums shrink-0">
          {r.price > 0 ? r.price.toFixed(2) : '0.00'}
        </span>
      </div>

      {/* Rejection reason */}
      {r.rejectionReason && (
        <div className="mt-1.5 flex gap-1.5 text-xs text-red-600 dark:text-red-400 font-bold">
          <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
          <span className="break-words min-w-0">{r.rejectionReason}</span>
        </div>
      )}

      {/* Row 2: Side + Qty + Time + Product */}
      <div className="flex items-center gap-3 mt-1.5 text-xs">
        <span className={cn('font-semibold', r.side === 'BUY' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400')}>
          {r.side}
        </span>
        <span className="text-gray-500 dark:text-gray-400">{r.qty} qty</span>
        <span className="text-gray-500 dark:text-gray-400">{r.product}</span>
        <span className="text-gray-400 tabular-nums">{formatTime(r.time)}</span>
      </div>

      {/* Expand toggle */}
      <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
        <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', expanded && 'rotate-180')} />
        {expanded ? 'Less' : 'More'}
      </button>

      {/* Expanded */}
      {expanded && (
        <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Client</span>
            <div className="text-gray-900 dark:text-white">{r.clientId}</div>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Broker</span>
            <div className="text-gray-900 dark:text-white">{r.broker}</div>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Basket</span>
            <div className="text-gray-900 dark:text-white truncate">{r.basketName || '—'}</div>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Strategy</span>
            <div className="text-gray-900 dark:text-white truncate">{r.strategyName || '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Order Table (desktop table + mobile cards) ---

function OrderTable({ orders, timeSortDir, onToggleTimeSort }: {
  orders: OrdersApiRow[];
  timeSortDir?: TimeSortDir;
  onToggleTimeSort?: () => void;
}) {
  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No Orders Found
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          No platform orders found in the broker orderbook.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile: Card layout */}
      <div className="md:hidden space-y-2 p-2">
        {orders.map((r, idx) => (
          <MobileOrderCard key={`${r.orderId || r.tag}-${idx}`} order={r} />
        ))}
      </div>

      {/* Desktop: Table layout */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Client</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>
                <button
                  onClick={onToggleTimeSort}
                  className="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Time
                  <ArrowUpDown className={cn('w-3 h-3', timeSortDir ? 'text-blue-500' : 'text-gray-400')} />
                </button>
              </th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Type</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Instrument</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Product</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Qty</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Price</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Status</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Basket</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Strategy</th>
              <th className={`${TH} text-gray-500 dark:text-gray-300`}>Tag</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {orders.map((r, idx) => (
              <React.Fragment key={`${r.orderId || r.tag}-${idx}`}>
              <tr
                className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {r.clientId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatTime(r.time)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold">
                  <span className={r.side === 'BUY' ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}>
                    {r.side}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{r.symbol}</span>
                  {r.exchange && (
                    <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{r.exchange}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {r.product}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white tabular-nums">
                  {r.qty}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white tabular-nums">
                  {r.price > 0 ? r.price.toFixed(2) : '0.00'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Badge variant={statusBadgeVariant[r.status] || 'default'}>
                    {statusLabels[r.status] || r.status}
                  </Badge>
                </td>
                <td className="px-6 py-4 max-w-[140px]">
                  <ScrollText text={r.basketName || '—'} />
                </td>
                <td className="px-6 py-4 max-w-[140px]">
                  <ScrollText text={r.strategyName || '—'} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {r.tag || '—'}
                </td>
              </tr>
              {r.rejectionReason && (
                <tr className="bg-red-50/50 dark:bg-red-900/10 border-b border-gray-200 dark:border-gray-700">
                  <td colSpan={11} className="px-6 py-2">
                    <div className="flex items-center justify-end gap-1.5 text-xs text-red-600 dark:text-red-400 font-bold">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                      {r.rejectionReason}
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


const OrdersPage: React.FC = () => {
  const brand = getBrandClasses();
  const query = useQuery();
  const navigate = useNavigate();
  const view = (query.get('view') as OrdersViewMode) || 'all';

  const [loading, setLoading] = React.useState(false);
  const [fetched, setFetched] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<OrdersApiResponse | null>(null);

  // Accordion state for grouped view
  const [expandedBaskets, setExpandedBaskets] = React.useState<Set<string>>(new Set());
  const [expandedStrategies, setExpandedStrategies] = React.useState<Set<string>>(new Set());

  const toggleBasket = (id: string) => {
    setExpandedBaskets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleStrategy = (id: string) => {
    setExpandedStrategies(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Filter state
  const [timeSortDir, setTimeSortDir] = React.useState<TimeSortDir>('desc');
  const toggleTimeSort = () => setTimeSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
  const [filterBroker, setFilterBroker] = React.useState('all');
  const [filterOrderStatus, setFilterOrderStatus] = React.useState('all');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [groupedPage, setGroupedPage] = React.useState(1);
  const groupedPageSize = 10;

  const setView = (next: OrdersViewMode) => {
    const params = new URLSearchParams(query);
    params.set('view', next);
    navigate({ search: params.toString() }, { replace: true });
  };

  const fetchOrderbook = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ordersService.fetchOrderbook();
      setPayload(data);
      setFetched(true);
    } catch (e: any) {
      setError(e?.message || 'Failed to fetch orderbook');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Phase 3.5: clear cached orderbook when admin switches "View as" so the
  // page surface obviously reflects the new scope (Orders doesn't auto-fetch
  // on mount — user clicks Fetch — so we don't auto-refetch on switch either,
  // but we DO drop the prior managed user's data to avoid confusion).
  const { actingAs } = useActingAs();
  React.useEffect(() => {
    setPayload(null);
    setFetched(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actingAs]);

  // Stabilise both derivations so downstream useMemos (uniqueBrokerClients,
  // rows filter, etc.) don't re-run every render when payload is unchanged.
  const allOrders = React.useMemo(() => payload?.orders || [], [payload]);
  const summary = React.useMemo(
    () => payload?.summary || { total: 0, complete: 0, rejected: 0, cancelled: 0, open: 0 },
    [payload],
  );

  // Extract unique broker-client combinations for dropdown
  const uniqueBrokerClients = React.useMemo(() => {
    const keys = new Set<string>();
    for (const o of allOrders) {
      if (o.broker && o.clientId) keys.add(`${o.broker}-${o.clientId}`);
    }
    return Array.from(keys).sort();
  }, [allOrders]);

  const orderStatusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'SUCCESS', label: 'Complete' },
    { value: 'OPEN', label: 'Open' },
    { value: 'FAILED', label: 'Rejected' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'PARTIAL', label: 'Trigger Pending' },
  ];

  const brokerOptions = React.useMemo(() => [
    { value: 'all', label: 'All Brokers' },
    ...uniqueBrokerClients.map(bc => {
      const [broker, ...clientParts] = bc.split('-');
      const clientId = clientParts.join('-');
      return { value: bc, label: `${broker.charAt(0).toUpperCase() + broker.slice(1)} - ${clientId}` };
    }),
  ], [uniqueBrokerClients]);

  // Apply filters
  const rows = React.useMemo(() => {
    let filtered = [...allOrders];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.symbol.toLowerCase().includes(q) ||
        o.strategyName.toLowerCase().includes(q) ||
        o.basketName.toLowerCase().includes(q) ||
        o.positionId.toLowerCase().includes(q) ||
        o.clientId.toLowerCase().includes(q) ||
        o.side.toLowerCase().includes(q) ||
        o.product.toLowerCase().includes(q) ||
        o.tag.toLowerCase().includes(q) ||
        (statusLabels[o.status] || o.status).toLowerCase().includes(q) ||
        String(o.qty).includes(q) ||
        String(o.price).includes(q) ||
        formatTime(o.time).includes(q)
      );
    }
    if (filterBroker !== 'all') {
      filtered = filtered.filter(o => `${o.broker}-${o.clientId}` === filterBroker);
    }
    if (filterOrderStatus !== 'all') {
      filtered = filtered.filter(o => o.status === filterOrderStatus);
    }
    filtered.sort((a, b) => {
      const cmp = a.time.localeCompare(b.time);
      return timeSortDir === 'asc' ? cmp : -cmp;
    });
    return filtered;
  }, [allOrders, searchQuery, filterBroker, filterOrderStatus, timeSortDir]);

  React.useEffect(() => { setGroupedPage(1); }, [searchQuery]);

  // Collapse All / Expand All helpers — use filtered rows for IDs
  const expandAll = () => {
    setExpandedBaskets(new Set(rows.map(r => r.basketId || '_unmatched')));
    setExpandedStrategies(new Set(rows.map(r => r.strategyId || '_unknown')));
  };

  const collapseAll = () => {
    setExpandedBaskets(new Set());
    setExpandedStrategies(new Set());
  };

  const content = (() => {
    if (loading) return <LoadingSpinner message="Fetching orderbook from brokers..." />;

    if (error) {
      return (
        <div className="space-y-3">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
          </div>
          <Button onClick={fetchOrderbook} size="sm">Retry</Button>
        </div>
      );
    }

    // Not yet fetched — show empty state
    if (!fetched) {
      return (
        <Card>
          <CardContent className="p-0">
            <div className="text-center py-12">
              <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Orders Loaded
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Click "Fetch Orderbook" to load today's orders from your brokers.
              </p>
              <Button onClick={fetchOrderbook} leftIcon={<BookOpen className="w-4 h-4" />}>
                Fetch Orderbook
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    // Info banner
    const infoBanner = (
      <div className={`${brand.selectionBar} border rounded-lg p-3 flex items-start gap-2`}>
        <Info className={`h-4 w-4 ${brand.iconText} mt-0.5 flex-shrink-0`} />
        <span className={`text-sm ${brand.selectionText}`}>
          Orderbook shows today's orders only. For historical data, check the{' '}
          <Link to="/schedule?view=positions" className={`underline ${brand.linkHover}`}>Schedule's Positions view</Link>.
        </span>
      </div>
    );

    const filterBar = (
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search orders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Broker Filter */}
          <Dropdown value={filterBroker} onChange={setFilterBroker} options={brokerOptions} />

          {/* Status Filter */}
          <Dropdown value={filterOrderStatus} onChange={setFilterOrderStatus} options={orderStatusOptions} />

          {/* Clear Filters */}
          <Button
            onClick={() => {
              setSearchQuery('');
              setFilterBroker('all');
              setFilterOrderStatus('all');
            }}
            variant="outline"
            size="sm"
          >
            Clear Filters
          </Button>

          {/* Refresh */}
          <Button onClick={fetchOrderbook} size="sm" leftIcon={<RefreshCw className="w-3.5 h-3.5" />} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>
    );

    const summaryCards = (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className={`h-4 w-4 ${brand.sortIcon} shrink-0`} />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</span>
          </div>
          <div className={`text-xl font-bold ${brand.link} tabular-nums`}>{summary.total}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Complete</span>
          </div>
          <div className="text-xl font-bold text-green-600 dark:text-green-400 tabular-nums">{summary.complete}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <Ban className="h-4 w-4 text-orange-500 shrink-0" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cancelled</span>
          </div>
          <div className="text-xl font-bold text-orange-600 dark:text-orange-400 tabular-nums">{summary.cancelled}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Rejected</span>
          </div>
          <div className="text-xl font-bold text-red-500 dark:text-red-400 tabular-nums">{summary.rejected}</div>
        </div>
      </div>
    );

    const viewToggle = (
      <div className="flex items-center gap-4">
        {view === 'byBasket' && (
          <div className="flex items-center gap-1">
            <Tooltip text="Expand All">
              <button
                onClick={expandAll}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronsUpDown className="w-4 h-4" />
              </button>
            </Tooltip>
            <Tooltip text="Collapse All">
              <button
                onClick={collapseAll}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronsDownUp className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        )}
        {/* View toggle — hidden for now, code preserved */}
        {false && (
        <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <Tooltip text="All Orders">
            <button
              onClick={() => setView('all')}
              className={cn('p-2 transition-colors', view === 'all' ? brand.activeBg : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700')}
            >
              <List className="w-4 h-4" />
            </button>
          </Tooltip>
          <Tooltip text="Group by Basket">
            <button
              onClick={() => setView('byBasket')}
              className={cn('p-2 transition-colors border-l border-gray-200 dark:border-gray-700', view === 'byBasket' ? brand.activeBg : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700')}
            >
              <ShoppingBasket className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
        )}
      </div>
    );

    if (view === 'all') {
      return (
        <div className="space-y-4">
          {infoBanner}
          {summaryCards}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Orders ({rows.length})
                </CardTitle>
                {viewToggle}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filterBar}
              <OrderTable orders={rows} timeSortDir={timeSortDir} onToggleTimeSort={toggleTimeSort} />
            </CardContent>
          </Card>
        </div>
      );
    }

    // Group by Basket — built from filtered rows so all filters apply
    const filteredBaskets = (() => {
      const basketMap = new Map<string, { basketId: string; basketName: string; strategies: Map<string, { strategyId: string; strategyName: string; orders: OrdersApiRow[] }> }>();
      for (const r of rows) {
        const bid = r.basketId || '_unmatched';
        if (!basketMap.has(bid)) {
          basketMap.set(bid, { basketId: bid, basketName: r.basketName || 'Unknown Basket', strategies: new Map() });
        }
        const basket = basketMap.get(bid)!;
        const sid = r.strategyId || '_unknown';
        if (!basket.strategies.has(sid)) {
          basket.strategies.set(sid, { strategyId: sid, strategyName: r.strategyName || 'Unknown Strategy', orders: [] });
        }
        basket.strategies.get(sid)!.orders.push(r);
      }
      const result = Array.from(basketMap.values()).map(b => ({
        basketId: b.basketId,
        basketName: b.basketName,
        strategies: Array.from(b.strategies.values()).sort((a, b2) => a.strategyName.localeCompare(b2.strategyName)),
      }));
      result.sort((a, b) => a.basketName.localeCompare(b.basketName));
      return result;
    })();


    if (filteredBaskets.length === 0) {
      return (
        <div className="space-y-4">
          {infoBanner}
          {summaryCards}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Orders ({rows.length})
                </CardTitle>
                {viewToggle}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {filterBar}
              <div className="text-center py-12">
                <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  No Orders Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  No platform orders found in the broker orderbook.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    const groupedTotalPages = Math.max(1, Math.ceil(filteredBaskets.length / groupedPageSize));
    const paginatedBaskets = filteredBaskets.slice((groupedPage - 1) * groupedPageSize, groupedPage * groupedPageSize);

    const groupedPaginationBar = filteredBaskets.length > groupedPageSize ? (
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          Showing {(groupedPage - 1) * groupedPageSize + 1}–{Math.min(groupedPage * groupedPageSize, filteredBaskets.length)} of {filteredBaskets.length} baskets
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setGroupedPage(p => Math.max(1, p - 1))}
            disabled={groupedPage === 1}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          {Array.from({ length: groupedTotalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === groupedTotalPages || Math.abs(p - groupedPage) <= 1)
            .reduce<(number | 'dots')[]>((acc, p, i, arr) => {
              if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('dots');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === 'dots' ? (
                <span key={`dots-${i}`} className="px-1 text-gray-400">...</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setGroupedPage(p)}
                  className={cn(
                    'min-w-[32px] h-8 rounded text-sm font-medium transition-colors',
                    p === groupedPage
                      ? brand.activeBg
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
                  )}
                >
                  {p}
                </button>
              )
            )}
          <button
            onClick={() => setGroupedPage(p => Math.min(groupedTotalPages, p + 1))}
            disabled={groupedPage === groupedTotalPages}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      </div>
    ) : null;

    return (
      <div className="space-y-4">
        {infoBanner}
        {summaryCards}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  Orders ({rows.length})
                </CardTitle>
              {viewToggle}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {filterBar}
            <div className="space-y-3 p-3">
              {paginatedBaskets.map((b) => {
                const basketOpen = expandedBaskets.has(b.basketId);
                const totalOrders = b.strategies.reduce((sum, s) => sum + s.orders.length, 0);

                return (
                  <div key={b.basketId} className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Basket header */}
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      onClick={() => toggleBasket(b.basketId)}
                    >
                      <div className="flex items-center gap-3">
                        <ChevronDown className={cn(
                          'w-4 h-4 text-gray-500 transition-transform duration-200',
                          basketOpen && 'rotate-180',
                        )} />
                        <div className="text-left">
                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{b.basketName}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {b.strategies.length} {b.strategies.length === 1 ? 'strategy' : 'strategies'} &middot; {totalOrders} orders
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Strategies inside basket */}
                    {basketOpen && (
                      <div className="border-t border-gray-200 dark:border-gray-700">
                        {b.strategies.map((s) => {
                          const stratOpen = expandedStrategies.has(s.strategyId);

                          return (
                            <div key={s.strategyId} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800">
                              {/* Strategy header */}
                              <button
                                className="w-full flex items-center justify-between px-4 py-2.5 pl-10 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                onClick={() => toggleStrategy(s.strategyId)}
                              >
                                <div className="flex items-center gap-2">
                                  <ChevronDown className={cn(
                                    'w-3.5 h-3.5 text-gray-400 transition-transform duration-200',
                                    stratOpen && 'rotate-180',
                                  )} />
                                  <span className="text-[13px] font-medium text-gray-700 dark:text-gray-200">{s.strategyName}</span>
                                </div>
                                <span className="text-xs text-gray-400 dark:text-gray-500">{s.orders.length} orders</span>
                              </button>

                              {/* Orders table inside strategy */}
                              {stratOpen && (
                                <div className="bg-white dark:bg-gray-800">
                                  <OrderTable orders={s.orders} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {groupedPaginationBar}
          </CardContent>
        </Card>
      </div>
    );
  })();

  return (
    <StandardLayout>
      <PageHeader
        title="Orders"
        pageType="management"
        icon={<ClipboardList className="w-5 h-5" />}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: <Home className="w-4 h-4" /> },
          { label: 'Orders', icon: <ClipboardList className="w-4 h-4" /> },
        ]}
        actions={
          fetched ? (
            <Button onClick={fetchOrderbook} size="sm" leftIcon={<RefreshCw className="w-3.5 h-3.5" />} disabled={loading}>
              {loading ? 'Fetching...' : 'Fetch Orderbook'}
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-4">
        {content}
      </div>
    </StandardLayout>
  );
};

export default OrdersPage;
